import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../Libs/firebase-config.mjs';

const HeroSection = ({ scrollToSection, darkMode }) => {
  const [heroData, setHeroData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadHeroData();
  }, []);

  const loadHeroData = async () => {
    try {
      const docRef = doc(db, 'heroSection', 'content');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setHeroData(docSnap.data());
      } else {
        // Fallback to default data if no data in Firestore
        setHeroData({
          title: 'Rev. Peter Kyengo',
          subtitle: 'Founder & Lead Minister of Faith Life Evangelistic Church',
          description: 'With over 20 years of active ministry experience, Rev. Peter Kyengo is an ordained minister, leadership consultant, and empathetic counselor. Through FLEC, he has established a growing presence across five Kenyan counties, bringing deliverance, healing, prosperity, and salvation through transformative teachings.',
          tagline: 'Ordained Minister · Leadership Consultant · Conference Speaker',
          primaryButton: 'Explore My Journey',
          secondaryButton: 'Get In Touch',
          imageUrl: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653729/photo_2025-04-02_16-36-04_g7mtdz.webp'
        });
      }
    } catch (err) {
      console.error('Error loading hero section:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className={`min-h-screen flex items-center ${darkMode ? 'bg-slate-800' : 'bg-slate-50'} pt-20 transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-8 py-20 w-full">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className={`h-4 bg-slate-300 rounded w-48 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                <div className={`h-16 bg-slate-300 rounded ${darkMode ? 'bg-slate-600' : ''}`}></div>
                <div className={`h-8 bg-slate-300 rounded w-64 ${darkMode ? 'bg-slate-600' : ''}`}></div>
              </div>
              <div className={`h-24 bg-slate-300 rounded ${darkMode ? 'bg-slate-600' : ''}`}></div>
              <div className="flex gap-4">
                <div className={`h-12 bg-slate-300 rounded w-40 ${darkMode ? 'bg-slate-600' : ''}`}></div>
                <div className={`h-12 bg-slate-300 rounded w-32 ${darkMode ? 'bg-slate-600' : ''}`}></div>
              </div>
            </div>
            <div className="relative">
              <div className={`w-full h-[600px] bg-slate-300 rounded-2xl ${darkMode ? 'bg-slate-600' : ''}`}></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className={`min-h-screen flex items-center ${darkMode ? 'bg-slate-800' : 'bg-slate-50'} pt-20 transition-colors duration-300`}>
        <div className="max-w-7xl mx-auto px-8 py-20 text-center">
          <div className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
            {error}
          </div>
        </div>
      </section>
    );
  }

  // Use fallback data if heroData is null
  const data = heroData || {
    title: 'Rev. Peter Kyengo',
    subtitle: 'Founder & Lead Minister of Faith Life Evangelistic Church',
    description: 'With over 20 years of active ministry experience, Rev. Peter Kyengo is an ordained minister, leadership consultant, and empathetic counselor. Through FLEC, he has established a growing presence across five Kenyan counties, bringing deliverance, healing, prosperity, and salvation through transformative teachings.',
    tagline: 'Ordained Minister · Leadership Consultant · Conference Speaker',
    primaryButton: 'Explore My Journey',
    secondaryButton: 'Get In Touch',
    imageUrl: 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653729/photo_2025-04-02_16-36-04_g7mtdz.webp'
  };

  return (
    <section className={`min-h-screen flex items-center ${darkMode ? 'bg-slate-800' : 'bg-slate-50'} pt-20 transition-colors duration-300`}>
      <div className="max-w-7xl mx-auto px-8 py-20">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-4"
            >
              <div className={`text-sm uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-600'} font-medium`}>
                {data.tagline || 'Ordained Minister · Leadership Consultant · Conference Speaker'}
              </div>
              <h1 className={`text-6xl md:text-7xl font-bold leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {data.title || 'Rev. Peter Kyengo'}
              </h1>
              <p className={`text-2xl ${darkMode ? 'text-slate-300' : 'text-slate-600'} font-light leading-relaxed`}>
                {data.subtitle || 'Founder & Lead Minister of Faith Life Evangelistic Church'}
              </p>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`text-lg ${darkMode ? 'text-slate-300' : 'text-slate-700'} leading-relaxed max-w-xl`}>
              {data.description || 'With over 20 years of active ministry experience, Rev. Peter Kyengo is an ordained minister, leadership consultant, and empathetic counselor. Through FLEC, he has established a growing presence across five Kenyan counties, bringing deliverance, healing, prosperity, and salvation through transformative teachings.'}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex gap-4"
            >
              <button 
                onClick={() => scrollToSection('journey')}
                className={`px-8 py-4 rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg hover:shadow-xl ${
                  darkMode 
                    ? 'bg-white text-slate-900 hover:bg-slate-100' 
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
              >
                {data.primaryButton || 'Explore My Journey'}
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => scrollToSection('connect')}
                className={`px-8 py-4 rounded-lg font-medium transition-all ${
                  darkMode 
                    ? 'border-2 border-white text-white hover:bg-white hover:text-slate-900' 
                    : 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                }`}
              >
                {data.secondaryButton || 'Get In Touch'}
              </button>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src={data.imageUrl || 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653729/photo_2025-04-02_16-36-04_g7mtdz.webp'} 
                alt={`${data.title || 'Rev. Peter Kyengo'} - ${data.subtitle || 'Founder and Lead Minister of Faith Life Evangelistic Church'}`}
                className="w-full h-[600px] object-cover"
                onError={(e) => {
                  // Fallback image if the main image fails to load
                  e.target.src = 'https://res.cloudinary.com/dbacioerk/image/upload/v1763653729/photo_2025-04-02_16-36-04_g7mtdz.webp';
                }}
              />
            </div>
            <div className={`absolute -bottom-8 -right-8 w-64 h-64 ${darkMode ? 'bg-white' : 'bg-slate-900'} rounded-2xl -z-10`} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;